// MindForge web — TS port of Modes/GameSession.swift.
//
// Plain class with mutable state. Wrapped in a React hook (useGameSession)
// that bumps a counter to force re-renders.

import { PlayerState } from "../state/playerState";
import {
  COUNTDOWN_FIRST_TRIAL_ONLY,
  MODE_COUNTDOWN_MS,
  MODE_ROUNDS,
  MODE_UNLOCKS,
} from "../state/modeConfig";
import {
  calcXpReward,
  calcErrorPenalty,
  shouldIncreaseDifficulty,
  StreakTracker,
} from "../logic/scoring";

export type GamePhase =
  | { kind: "instructions" }
  | { kind: "countdown" }
  | { kind: "playing" }
  | { kind: "feedback"; correct: boolean }
  | { kind: "results" };

export interface SessionResult {
  score: number;
  xpEarned: number;
  correctCount: number;
  totalRounds: number;
  bestStreak: number;
  difficultyIncreased: boolean;
  newUnlocks: string[];
}

export class GameSession {
  readonly modeId: string;
  readonly maxRounds: number;
  readonly playerState: PlayerState;
  /** Externally supplied save function — called from enterResults. */
  private readonly persist: () => void;

  phase: GamePhase = { kind: "instructions" };
  roundNum = 0;
  score = 0;
  xpEarned = 0;
  correctCount = 0;
  wrongCount = 0;
  streak = new StreakTracker();
  newUnlocks: string[] = [];
  difficultyIncreased = false;

  /** Mutable so adaptive-N can bump it mid-session. */
  difficulty: number;
  private readonly initialLevel: number;
  /** Has any trial begun this session? (gates the first-trial-only countdown) */
  private roundStarted = false;

  constructor(
    modeId: string,
    playerState: PlayerState,
    persist: () => void,
  ) {
    this.modeId = modeId;
    this.playerState = playerState;
    this.persist = persist;
    this.maxRounds = MODE_ROUNDS[modeId] ?? 10;
    this.difficulty = playerState.getModeDifficulty(modeId);
    this.initialLevel = playerState.level;
  }

  startPlaying(): void {
    this.beginRound();
  }

  /**
   * Single entry point into a fresh trial — from the Start button (first trial)
   * and from the end of feedback (every subsequent trial). Shows a "get ready"
   * countdown first when the mode is configured for one; otherwise goes straight
   * to playing. Presentation-only: scoring/round accounting is untouched, so the
   * stimulus and response timers (which the mode starts on the "playing" phase)
   * never tick during the countdown.
   */
  private beginRound(): void {
    const ms = MODE_COUNTDOWN_MS[this.modeId] ?? 0;
    const first = !this.roundStarted;
    this.roundStarted = true;
    const firstOnly = COUNTDOWN_FIRST_TRIAL_ONLY.has(this.modeId);
    if (ms > 0 && (first || !firstOnly)) {
      this.phase = { kind: "countdown" };
    } else {
      this.phase = { kind: "playing" };
    }
  }

  /** Called by the countdown timer (or a skip tap) to begin the trial. */
  finishCountdown(): void {
    if (this.phase.kind !== "countdown") return;
    this.phase = { kind: "playing" };
  }

  /** Called by the mode component when the round is answered. */
  submitAnswer(correct: boolean): void {
    // Guard against double-submit. A mode's recall-countdown timer keeps its
    // own subPhase as "recalling" after a manual submit, so it can fire a
    // second submitAnswer during the feedback window (e.g. the player taps
    // Submit ~1s before the timer expires). Ignore any call that arrives when
    // we're not actively playing — the first submit already moved us to
    // feedback, and a second would double-count XP and the round number.
    if (this.phase.kind !== "playing") return;

    if (correct) {
      this.streak.hit();
      const xp = calcXpReward(this.streak.current, this.difficulty);
      this.playerState.addXp(xp);
      this.score += xp;
      this.xpEarned += xp;
      this.correctCount += 1;
    } else {
      this.streak.miss();
      this.wrongCount += 1;
      const penalty = calcErrorPenalty(this.difficulty);
      this.playerState.deductXp(penalty);
      this.xpEarned -= penalty;
    }

    this.roundNum += 1;
    this.phase = { kind: "feedback", correct };
  }

  /** Called by mode components or the feedback timer to leave feedback. */
  advanceFromFeedback(): void {
    if (this.phase.kind !== "feedback") return;
    if (this.roundNum >= this.maxRounds) {
      this.enterResults();
    } else {
      this.beginRound();
    }
  }

  /**
   * Dual Task uses this directly when the player gets a math problem wrong
   * — penalty applies immediately, not waiting for the round to end.
   */
  applyMidRoundPenalty(penalty: number): void {
    if (penalty <= 0) return;
    this.playerState.deductXp(penalty);
    this.score = Math.max(0, this.score - penalty);
    this.xpEarned = Math.max(0, this.xpEarned - penalty);
    this.streak.miss();
  }

  private enterResults(): void {
    this.phase = { kind: "results" };
    this.difficultyIncreased = shouldIncreaseDifficulty(
      this.correctCount,
      this.maxRounds,
    );
    if (this.difficultyIncreased) {
      this.playerState.increaseDifficulty(this.modeId);
    }
    this.playerState.updateHighScore(this.modeId, this.score);
    this.playerState.sessionsPlayed += 1;

    // Compute unlocks from net level change across the whole session —
    // avoids celebrating an unlock that mid-session penalties revoked.
    const endLevel = this.playerState.level;
    if (endLevel > this.initialLevel) {
      this.newUnlocks = Object.entries(MODE_UNLOCKS)
        .filter(([, lv]) => this.initialLevel < lv && lv <= endLevel)
        .map(([id]) => id);
    }

    this.persist();
  }

  get sessionResult(): SessionResult {
    return {
      score: this.score,
      xpEarned: this.xpEarned,
      correctCount: this.correctCount,
      totalRounds: this.maxRounds,
      bestStreak: this.streak.best,
      difficultyIncreased: this.difficultyIncreased,
      newUnlocks: this.newUnlocks,
    };
  }
}
